import React, { useEffect, useRef } from "react";
import { useRoute } from "@react-navigation/native";
import { RootRouteProps } from "routers/types/stack";

import { TextInput } from "react-native";
import {
  Input,
  Error,
  Button,
  Header,
  Loading,
  TextArea,
  InputLabel,
} from "components";
import { useForm, Controller } from "react-hook-form";

import { colors, sizes, strings } from "assets/constants";
import { alertPermission, mq } from "utils";

import { useNote, useAddNote, useEditNote, useDeleteNote } from "clients/apis";
import DetailsTemplate from "screens/components/DetailsTemplate";
import { _goToBack, _goToCustomerNotes, _pop } from "routers/helper";
import { useChmod } from "hooks";
import { useRecoilValue } from "recoil";
import { authAtom } from "stores";

interface FormDataT {
  title?: string;
  descriptions?: string;
}

export default function Note() {
  const { chmod } = useChmod();
  const auth = useRecoilValue(authAtom);

  const {
    customer,
    data: routeParamData,
    popCount,
  } = useRoute<RootRouteProps<"note">>().params;

  const initial = routeParamData as NoteT;
  const headerTitle = initial
    ? strings?.edit_note + " " + (initial?.customer?.name ?? "")
    : strings.add_note_for + " " + customer?.name;

  const { isFetching, data, error, isError, refetch, isRefetching } = useNote(
    initial?.id
  );

  const titleInput = useRef<TextInput>();
  const descriptionsInput = useRef<TextInput>();

  const {
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<FormDataT>({
    defaultValues: { title: "", descriptions: "" },
  });

  const _onDoneTitleInput = () => descriptionsInput.current?.focus();

  const _goToThisCustomerNotes = () => {
    initial
      ? initial?.customer_id
        ? _goToCustomerNotes({
            id: initial?.customer_id,
            name: initial?.customer?.name?.trim() ?? strings.deleted_customer,
          })
        : _goToCustomerNotes({ id: auth?.id, name: strings.myself })
      : _goToCustomerNotes({ id: customer?.id, name: customer?.name });
  };

  useEffect(() => {
    if (data && initial) {
      setValue("title", data.data?.title);
      setValue("descriptions", data.data?.description);
    } else {
      setValue("title", "");
      setValue("descriptions", "");
    }
  }, [data, setValue]);

  const addNoteMut = useAddNote();
  const editNoteMut = useEditNote();
  const deleteNoteMut = useDeleteNote();

  const _onSubmit = () => {
    const postData: NoteReqT = {
      title: getValues().title,
      customer_id: customer?.id ?? null,
      description: getValues().descriptions,
    };

    addNoteMut.mutate(postData, {
      onSuccess: () => _pop(popCount ? popCount : 2),
    });
  };

  const _onEdit = () => {
    const { title, descriptions } = getValues();
    const { title: dTitle, description } = data?.data;

    if (title === dTitle && descriptions === description) {
      _goToBack();
      return;
    }

    const postData: NoteReqT = {
      title: getValues().title,
      description: getValues().descriptions,
    };
    editNoteMut.mutate({ id: data?.data?.id, data: postData });
  };

  const _onDeleteConfirm = (close: () => void) =>
    deleteNoteMut.mutate(data?.data.id, { onSuccess: close });

  const _renderContent = () => {
    if (isFetching) {
      return <Loading style={{ marginTop: mq(20) }} />;
    }

    if (isError) {
      return <Error error={error} reset={refetch} loading={isRefetching} />;
    }

    return (
      <DetailsTemplate
        onDelete={_onDeleteConfirm}
        deletable={data && chmod("note", "DELETE") ? true : false}
        deleteLoading={deleteNoteMut?.isLoading}
        submitLabel={!data ? strings.submit_info : strings.edit_info}
        submitLoading={addNoteMut?.isLoading || editNoteMut?.isLoading}
        onSubmit={
          data
            ? chmod("note", "PUT")
              ? handleSubmit(_onEdit)
              : alertPermission
            : chmod("note", "POST")
            ? handleSubmit(_onSubmit)
            : alertPermission
        }
      >
        <>
          <InputLabel label={strings.title} />
          <Controller
            control={control}
            name="title"
            rules={{
              required: { value: true, message: strings.fill_input },
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <Input
                inputRef={titleInput}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                placeholder={strings.insert_title}
                icon={{ name: "edit-3" }}
                onEndEditing={_onDoneTitleInput}
                error={errors.title?.message as string}
              />
            )}
          />

          <InputLabel label={strings.descriptions} />
          <Controller
            control={control}
            name="descriptions"
            render={({ field: { onChange, onBlur, value } }) => (
              <TextArea
                inputRef={descriptionsInput}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                placeholder={strings.insert_description}
                icon={{ name: "align-right" }}
              />
            )}
          />
        </>
      </DetailsTemplate>
    );
  };

  return (
    <>
      <Header title={headerTitle} />

      <Button
        onPress={_goToThisCustomerNotes}
        width={"auto"}
        bgColor="transparent"
        title={{ value: strings.view_all_notes, color: colors.theme }}
        style={{ alignSelf: "flex-start", paddingHorizontal: sizes.space }}
      />

      {_renderContent()}
    </>
  );
}
